//
// Definitions
//

import {
	shirt,
	shirtOutline,
	rocket,
	rocketOutline,
	body,
	bodyOutline,
	diamond,
	diamondOutline,
	calendar,
	analyticsOutline,
	informationCircleOutline,
} from 'ionicons/icons'
import { players } from './media/teamIcon'
import { training } from './media/trainingIcon'
import { BENCHED, PREFS_REQUIRE, queryFieldPositions } from './algorithm/constants'

export { BENCHED, defaultNumPeriods, NULL_NETSTAT_VALUE, PREFS_REQUIRE, queryFieldPositions, queryMaxIdealPositions } from './algorithm/constants'

export const RAIN 							= 'nulled' as const

export const ROSTER_NOT_AVAILABLE 			= 'na'

export const queryFieldPositionsPlusBench = (gameFormat: GameFormat) => {
	return [
		...queryFieldPositions(gameFormat),
		BENCHED,
	] as const
}

// Unfortunately this is duplicated in CSS
const fieldPositionColors = [
	'#65b965', '#00c097', '#00bac0', '#60ade0', '#ab9dec', '#e38edf', '#ff86c0', '#EEEEEE',
] as const
const fieldPositionColors_6ASideNoWD = [
	'#65b965', '#00c097', '#00bac0', '#60ade0', '#e38edf', '#ff86c0', '#EEEEEE',
] as const
const fieldPositionColors_6ASideNoC = [
	'#65b965', '#00c097', '#00bac0', '#ab9dec', '#e38edf', '#ff86c0', '#EEEEEE',
] as const
const fieldPositionColors_Fast5 = [
	'#65b965', '#00c097', '#60ade0', '#e38edf', '#ff86c0', '#EEEEEE',
] as const
const fieldPositionColors_6ASide = [
	'#00c097', '#00c097', '#60ade0', '#60ade0', '#ff86c0', '#ff86c0', '#EEEEEE',
] as const
const fieldPositionColors_5ASide = [
	'#00c097', '#00c097', '#60ade0', '#ff86c0', '#ff86c0', '#EEEEEE',
] as const

export const queryFieldPositionColors = (gameFormat: GameFormat) => {
	switch (gameFormat) {
		case 4: return fieldPositionColors_Fast5
		case 5: return fieldPositionColors_5ASide
		case 6: return fieldPositionColors_6ASide
		case 8: return fieldPositionColors_6ASideNoWD
		case 9: return fieldPositionColors_6ASideNoC
		default: return fieldPositionColors
	}
}

export const NORMALIZE_MIN_QTRS				= 6 as const

export const NORMALIZE_MIN_QTRS_FOR_PAIRS 	= {
	pairs: 6,
	trios: 5,
} as const

export const HOME_HOSTNAME 					= 'autoroster.io' as const
export const HOME_URL 						= `https://${HOME_HOSTNAME}` as const
export const APP_STORE_URL 					= 'https://apps.apple.com/au/app/autoroster-for-netball/id1642680492' as const
export const GOOGLE_PLAY_STORE_URL          = 'https://play.google.com/store/apps/details?id=com.maxbarry.autoroster' as const

export const SIDES 							= [ 'me', 'them' ] as const

export const PAIR_MODES 					= [ 'pairs', 'trios' ] as const

export const tabButtons = [
    {
        id: 'players',
        label: 'Players',
        icon: players,
		requireUser: false,
    },
	{
		id: 'training',
		label: 'Training',
		icon: training,
		requireUser: false,
	},
	{
		id: 'games',
		label: 'Games',
		icon: calendar,
		requireUser: false,
	},
	{
		id: 'stats',
        label: 'Stats',
        icon: analyticsOutline,
		requireUser: false,
    },

    {
        id: 'help',
        label: 'Help',
        icon: informationCircleOutline,
		requireUser: false,
    },
] as const

export const otherValidSections 					= [ 'join-club', 'settings' ] as const

export const isValidTab = (id: string): boolean => !!tabButtons.find(tab => id === tab.id) || (otherValidSections as readonly string[]).includes(id)

//
// Stats
//

// Combo must have been used at least this many times to appear in stats
export const MIN_NUM_QTRS_PER_COMBO		= 2 as const

//
// Settings.jsx
//

export const LOCAL_STORAGE_KEY_SETTINGS = 'settings' as const

const PRESET_DEVELOPMENT			= 'DEVELOPMENT' as const
const PRESET_JUNIOR					= 'JUNIOR' as const
const PRESET_SENIOR					= 'SENIOR' as const
export const PRESET_CUSTOM			= 'CUSTOM' as const

export const defaultPreset			= 'JUNIOR'

export const presets = {
	[PRESET_DEVELOPMENT]: {
		name: 'Development',
		order: 1,
		tooltip: "Development players are given more positional variety, and simple bib changes are prioritized.",
        icon: body,
        iconOutline: bodyOutline,
		defaults: {
			multiplePositions: 5,
			centerExhaustion: 2,
			backsies: 5,
			directSwaps: 5,
			halfTimeSwaps: 5,
			useRollingSubs: 0,
			benchEvenly: 8,
			evenSeason: 7,
			netStats: 0,
			idealNumPositions: 3,
		},
	},
	[PRESET_JUNIOR]: {
		name: 'Junior',
		order: 2,
		tooltip: "Junior teams mix optimal team performance with some positional variety.",
        icon: rocket,
        iconOutline: rocketOutline,
		defaults: {
			multiplePositions: 5,
			centerExhaustion: 3,
			backsies: 2,
			directSwaps: 0,
			halfTimeSwaps: 0,
			useRollingSubs: 2,
			benchEvenly: 7,
			evenSeason: 0,
			netStats: 5,
			idealNumPositions: 2,
		},
	},
	[PRESET_SENIOR]: {
		name: 'Senior',
		order: 3,
		tooltip: "For Senior players, team performance is prioritized.",
        icon: diamond,
        iconOutline: diamondOutline,
		defaults: {
			multiplePositions: 3,
			centerExhaustion: 3,
			backsies: 0,
			directSwaps: 0,
			halfTimeSwaps: 0,
			useRollingSubs: 3,
			benchEvenly: 5,
			evenSeason: 0,
			netStats: 7,
			idealNumPositions: 2,
		},
	},
	[PRESET_CUSTOM]: {
		name: 'Custom',
		order: 4,
		tooltip: "Your own preferences.",
        icon: shirt,
        iconOutline: shirtOutline,
		defaults: {
			multiplePositions: 5,
			centerExhaustion: 5,
			backsies: 5,
			directSwaps: 5,
			halfTimeSwaps: 5,
			useRollingSubs: 5,
			benchEvenly: 5,
			evenSeason: 5,
			netStats: 5,
			idealNumPositions: 2,
		},
	}
} as const

export const sliderSettings = [
	{
		id: 'netStats',
		desc: 'Optimize team performance',
        tip: 'Put players in positions that correlate with team success. Available when you\'ve entered quarter-by-quarter scores for at least one completed game.',
		default: 5,
	},
	{
		id: 'benchEvenly',
		desc: 'Bench players evenly',
		tip: 'Give all players the same amount of game time.',
		default: 5,
	},
	{
		id: 'centerExhaustion',
		desc: 'Avoid exhaustion',
		tip: 'Give more breaks to players in harder-running positions, especially Centre.',
		default: 5,
	},
    {
		id: 'useRollingSubs',
		desc: 'Use rolling subs',
		tip: 'Make more mid-quarter substitutions.',
		default: 5,
	},
	{
		id: 'idealNumPositions',
		desc: 'Ideal number of player positions',
		tip: 'The number of different positions each player should have per game (e.g. 2). The slider sets how important it is for AutoRoster to hit this number.',
		default: 2,
	},
	{
		id: 'multiplePositions',
		desc: '└▶',
		tip: undefined, // 'How important it is for all players to have the ideal number of different positions.',
		default: 5,
	},
    {
        id: 'backsies',
        desc: 'Avoid backsies',
        tip: 'Avoid switching away from a position then back into it.',
        default: 5,
    },
    {
        id: 'directSwaps',
        desc: 'Prefer direct bib swaps',
        tip: 'Players swap in pairs, with each taking the other\'s position.',
        default: 5,
    },
    {
        id: 'halfTimeSwaps',
        desc: 'Prefer half-time changes',
        tip: 'Players change positions at half-time, rather than after the 1st and 3rd quarters.',
        default: 5,
    },
    {
        id: 'evenSeason',
        desc: 'Rotate positions throughout season',
        tip: 'Over the course of the season, give all players equal time in each position (including bench).',
        default: 5,
    },

] as const

export const SEASON_RULE_STANDARD = 0 as const
export const SEASON_RULE_MALE_MIXED = 1 as const

export const seasonNumPeriodsOptions = [
	{ value: 1, label: '1 Period' },
	{ value: 2, label: '2 Halves' },
	{ value: 3, label: '3 Periods' },
	{ value: 4, label: '4 Quarters' },
	{ value: 5, label: '5 Periods' },
	{ value: 6, label: '6 Periods' },
] as const

export const seasonGameFormatOptions = [
	{ value: 7, label: '7 players - standard' },
	{ value: 8, label: '6 players - no WD' },
	{ value: 9, label: '6 players - no C' },
	{ value: 6, label: '6 players - A/C/D' },
	{ value: 5, label: '5 players - A/C/D' },
	{ value: 4, label: '5 players - no Wings' },
] as const

export const seasonPrivacyOptions = [
	{ value: 0, label: 'Shareable' },
	{ value: 1, label: 'Private' },
] as const

export const seasonRulesOptions = [
	{
		value: SEASON_RULE_STANDARD,
		label: 'Standard',
	},
	{
		value: SEASON_RULE_MALE_MIXED,
		label: '1 Male per Third',
	},
] as const

export const PREFS_PREFER 		= 'Prefer' as const
export const ALL_PREFS = [ PREFS_PREFER, PREFS_REQUIRE ] as const
export const FLOW_COLOR_PRESETS = [
	'#ff4242', '#ff7900', '#FFB300', '#43A047',
	'#1E88E5', '#3949AB', '#8E24AA', '#FF80AB',
	'#cd7150', '#888888',
] as const

export const dbids = {
	club: '/clubs',
	team: '/teams',
	season: '/teams/__TEAM__/seasons',
	player: '/teams/__TEAM__/seasons/__SEASON__/players',
	game: '/teams/__TEAM__/seasons/__SEASON__/games',
	roster: '/teams/__TEAM__/seasons/__SEASON__/rosters',
	flow: '/teams/__TEAM__/seasons/__SEASON__/flows',
	training: '/teams/__TEAM__/seasons/__SEASON__/training',
} as const
